/**
 * 图片加载逻辑
 */
import {
  MAX_CONCURRENT_LOAD_TASKS,
  LOAD_TIMEOUT_MS,
  RELOAD_POLL_INTERVAL_MS,
  RELOAD_STATUS_POLL_INTERVAL_MS,
  RELOAD_MAX_POLL_TIME_MS,
  STATUS_CHECK_EVERY_N_ATTEMPTS,
  RELOAD_MAX_POLL_ATTEMPTS,
  RESTORE_BATCH_SIZE,
  POLL_PROGRESS_MAX_PENDING
} from '../utils/constants';
import ImageReloader from '@/components/st_comps/st_comp/utils/ImageReloader';

/**
 * 判断是否为审核目标图片
 * @param {*} image - 图片对象
 * @returns {boolean}
 */
function isAuditTarget(image) {
  return Boolean(image && typeof image === 'object' && image.im);
}

function getCleanUrl(url) {
  if (typeof url !== 'string') return '';
  return url.split('?')[0];
}

/**
 * 按 restore_images 返回状态对 errorImages 分类
 * @param {Array} errorImages - 阶段一加载失败的图片列表
 * @param {Array} restoreResults - restore_images 接口返回结果
 * @returns {Object} { archiveImages, notArchiveImages, failedImages }
 */
export function categorizeRestoreResults(errorImages, restoreResults) {
  const statusMap = new Map();
  if (Array.isArray(restoreResults)) {
    restoreResults.forEach((result) => {
      if (result && result.url) {
        statusMap.set(getCleanUrl(result.url), result.status);
      }
    });
  }

  const archiveImages = [];
  const notArchiveImages = [];
  const failedImages = [];

  (errorImages || []).forEach((item) => {
    const lookupUrl = getCleanUrl(item.url);
    const status = statusMap.get(lookupUrl);

    if (status === 'restored' || status === 'restoring') {
      archiveImages.push(item);
    } else if (status === 'failed') {
      failedImages.push(Object.assign({}, item, { status }));
    } else {
      // not_archive 或未知状态
      notArchiveImages.push(Object.assign({}, item, {
        status: status || 'unknown'
      }));
    }
  });

  return { archiveImages, notArchiveImages, failedImages };
}

export function useImageLoader() {
  const activeLoadTimeouts = new Set();

  return {
    /**
     * 开始加载图片队列
     */
    startLoadQueue(
      images,
      { onUpdate, onProgress, onStatusChange, onEnd, onReloadPolling, onAbnormal, onTimeout },
      tokenRef,
      isTokenValid
    ) {
      const loadQueue = Array.isArray(images) ? images.map((image, index) => ({ image, index })) : [];
      const loadToken = Date.now();
      tokenRef.current = loadToken;

      if (!loadQueue.length) {
        if (typeof onEnd === 'function') {
          onEnd();
        }
        return { loadToken };
      }

      let runningCount = 0;
      let nextTaskIndex = 0;
      let loadFinishedCount = 0;
      const loadTotalCount = loadQueue.length;
      const errorImages = [];

      const canContinue = () => isTokenValid(loadToken);

      const finalizeTask = () => {
        if (!canContinue()) {
          return;
        }

        runningCount = runningCount > 0 ? runningCount - 1 : 0;
        loadFinishedCount++;

        if (typeof onProgress === 'function') {
          onProgress(loadFinishedCount, loadTotalCount);
        }

        if (loadFinishedCount === loadTotalCount && runningCount === 0 && nextTaskIndex >= loadQueue.length) {
          if (errorImages.length > 0) {
            if (typeof onReloadPolling === 'function') {
              onReloadPolling(errorImages);
            }
            return;
          }
          if (typeof onEnd === 'function') {
            onEnd();
          }
          return;
        }

        pumpQueue();
      };

      const handleLoadComplete = (image, imageObject, isError, index, reason = 'onerror') => {
        if (!canContinue()) {
          return;
        }

        if (isError) {
          // 阶段1：加载失败，收集 errorImages，不设置 orisize
          errorImages.push({
            image: image,
            imageObject: imageObject,
            url: image.im || image.src || image,
            index: index,
            reason: reason
          });
          finalizeTask();
          return;
        }

        if (isAuditTarget(image)) {
          image.orisize = [imageObject.width, imageObject.height];
        }

        if (typeof onUpdate === 'function') {
          onUpdate(image, imageObject, false);
        }
        finalizeTask();
      };

      const runLoadTask = (task) => {
        let im = null;
        let timeoutId = null;
        let completed = false;
        const image = task.image;

        const finish = (imageObject, isError, reason = 'onerror') => {
          if (completed) {
            return;
          }
          completed = true;
          if (timeoutId) {
            clearTimeout(timeoutId);
            activeLoadTimeouts.delete(timeoutId);
            timeoutId = null;
          }
          // 清理 Image 对象，避免超时后继续下载和内存占用
          if (im) {
            im.onload = null;
            im.onerror = null;
            im.src = '';
          }
          handleLoadComplete(image, imageObject, isError, task.index, reason);
        };

        try {
          if (typeof onStatusChange === 'function') {
            onStatusChange('正在加载图片');
          }

          const imageUrl = isAuditTarget(image) ? image.im : image;
          im = new Image();

          im.onload = function () {
            finish(this, false);
          };

          im.onerror = function () {
            finish(this, true, 'onerror');
          };

          timeoutId = setTimeout(() => {
            if (typeof onTimeout === 'function') {
              onTimeout(image, imageUrl);
            }
            finish(im || {}, true, 'timeout');
          }, LOAD_TIMEOUT_MS);
          activeLoadTimeouts.add(timeoutId);

          im.src = imageUrl;
        } catch (err) {
          if (typeof onAbnormal === 'function') {
            onAbnormal(err, image);
          }
          // 异常也视为加载失败，避免卡死
          finish(im || {}, true, 'onerror');
        }
      };

      const pumpQueue = () => {
        if (!canContinue()) {
          return;
        }

        while (runningCount < MAX_CONCURRENT_LOAD_TASKS && nextTaskIndex < loadQueue.length) {
          const task = loadQueue[nextTaskIndex];
          nextTaskIndex++;
          runningCount++;
          runLoadTask(task);
        }
      };

      pumpQueue();

      return { loadToken };
    },

    /**
     * 阶段2：轮询重载（混合轮询）
     * @param {Array} imagesToReload - 需要轮询的 errorImages
     * @param {Object} options - { onUpdate, onEnd, onAttempt, onRound, onAbnormal, requestFn }
     * @returns {ImageReloader} - 返回实例，便于外部 destroy
     */
    reloadImagesWithPolling(imagesToReload, { onUpdate, onEnd, onAttempt, onRound, onAbnormal, requestFn }) {
      const reloader = new ImageReloader({
        pollInterval: RELOAD_POLL_INTERVAL_MS,
        statusPollInterval: RELOAD_STATUS_POLL_INTERVAL_MS,
        maxPollTime: RELOAD_MAX_POLL_TIME_MS,
        loadTimeout: LOAD_TIMEOUT_MS,
        maxPollAttempts: RELOAD_MAX_POLL_ATTEMPTS,
        statusCheckEveryNAttempts: STATUS_CHECK_EVERY_N_ATTEMPTS,
        pollProgressMaxPending: POLL_PROGRESS_MAX_PENDING
      });

      const queryRestoreStatus = (items) => this.queryRestoreStatus(items, requestFn);

      reloader.reloadBatch(imagesToReload, {
        onItemSuccess: (item, imageObject) => {
          if (typeof onUpdate === 'function') {
            onUpdate(item.image, imageObject, false);
          }
        },
        onItemError: (item) => {
          if (typeof onUpdate === 'function') {
            // 超时/永久失败已设置 orisize=[300,300]，imageObject 为阶段1 的实例
            onUpdate(item.image, item.imageObject, true);
          }
        },
        onAttempt: (item, attempts, isSuccess, timedOut, reason) => {
          if (typeof onAttempt === 'function') {
            onAttempt(item, attempts, isSuccess, timedOut, reason);
          }
        },
        onRound: (attempts) => {
          if (typeof onRound === 'function') {
            onRound(attempts);
          }
        },
        onError: (err) => {
          if (typeof onAbnormal === 'function') {
            onAbnormal(err);
          }
        },
        onEnd: (preheatResults) => {
          reloader.destroy();
          if (typeof onEnd === 'function') {
            onEnd(preheatResults);
          }
        },
        queryRestoreStatus
      }).catch((err) => {
        reloader.destroy();
        if (typeof onAbnormal === 'function') {
          onAbnormal(err);
        }
        if (typeof onEnd === 'function') {
          onEnd([]);
        }
      });

      return reloader;
    },

    /**
     * 取消所有活跃的 stage 1 加载超时定时器
     */
    cancelAllLoadTimeouts() {
      activeLoadTimeouts.forEach((id) => clearTimeout(id));
      activeLoadTimeouts.clear();
    },

    /**
     * 阶段2前置：批量请求归档图片解冻
     */
    async restoreArchivedImages(imagesToReload, requestFn, options = {}) {
      if (!Array.isArray(imagesToReload) || imagesToReload.length === 0) {
        return [];
      }

      const urls = imagesToReload.map(item => item.url).filter(Boolean);
      if (urls.length === 0) {
        return [];
      }

      if (typeof requestFn !== 'function') {
        return [];
      }

      const { onAbnormal } = options;
      const results = [];
      const errors = [];

      for (let i = 0; i < urls.length; i += RESTORE_BATCH_SIZE) {
        const batch = urls.slice(i, i + RESTORE_BATCH_SIZE);
        const batchIndex = Math.floor(i / RESTORE_BATCH_SIZE);

        try {
          const response = await requestFn('/imgcenter/restore_images', {
            urls: JSON.stringify(batch)
          });

          if (response && response.errcode === 0 && Array.isArray(response.data)) {
            results.push(...response.data);
          } else {
            const error = new Error('restore_images_batch_failed');
            errors.push({ batchIndex, batch, response });
            if (typeof onAbnormal === 'function') {
              onAbnormal(error, { batchIndex, batch, response });
            }
          }
        } catch (err) {
          errors.push({ batchIndex, batch, error: err });
          if (typeof onAbnormal === 'function') {
            onAbnormal(err, { batchIndex, batch });
          }
        }
      }

      // 如果全部批次都失败，抛出聚合错误，保持与原 .catch 兼容
      if (results.length === 0 && errors.length > 0) {
        const aggregateError = new Error('all_restore_images_batches_failed');
        aggregateError.errors = errors;
        throw aggregateError;
      }

      return results.map(item => ({
        url: item.url,
        from: item.from,
        provider: item.provider,
        bucket: item.bucket,
        path: item.path,
        status: item.status,
        msg: item.msg
      }));
    },

    /**
     * 查询解冻状态：GET /imgcenter/restore_status（只读）
     * @param {Array} items - 待查询图片项，每项包含 { url }
     * @param {Function} requestFn - (url, params) => Promise
     * @returns {Promise<{ results: Array, errors: Array }>}
     */
    async queryRestoreStatus(items, requestFn) {
      if (!Array.isArray(items) || items.length === 0) {
        return { results: [], errors: [] };
      }

      const urls = items.map(item => item.url).filter(Boolean);
      if (urls.length === 0) {
        return { results: [], errors: [] };
      }

      if (typeof requestFn !== 'function') {
        return { results: [], errors: [] };
      }

      const results = [];
      const errors = [];

      for (let i = 0; i < urls.length; i += RESTORE_BATCH_SIZE) {
        const batch = urls.slice(i, i + RESTORE_BATCH_SIZE);
        const batchIndex = Math.floor(i / RESTORE_BATCH_SIZE);

        try {
          const response = await requestFn('/imgcenter/restore_status', {
            urls: JSON.stringify(batch)
          });

          const statusResults = Array.isArray(response)
            ? response
            : (response && response.errcode === 0 && Array.isArray(response.data) ? response.data : null);

          if (statusResults) {
            results.push(...statusResults);
          } else {
            errors.push({ batchIndex, batch, response });
          }
        } catch (err) {
          errors.push({ batchIndex, batch, error: err });
        }
      }

      return { results, errors };
    }
  };
}
